import { routeQuery        } from './agents/routerAgent';
import { runAmazonAgent    } from './agents/amazonAgent';
import { runUltaAgent      } from './agents/ultaAgent';
import { runSephoraAgent   } from './agents/sephoraAgent';
import { runMacysAgent     } from './agents/macysAgent';
import { runWalmartAgent   } from './agents/walmartAgent';
import { runHomedepotAgent } from './agents/homedepotAgent';
import { runTargetAgent    } from './agents/targetAgent';

// ─── Officially supported stores ──────────────────────────────────
export const SUPPORTED_IDS = ['amazon', 'walmart', 'homedepot', 'target'];

// ─── Known store catalog with colors ──────────────────────────────
export const KNOWN_STORES = [
  { id: 'amazon',    name: 'Amazon',     color: '#ff9900', bg: 'rgba(255,153,0,.1)',  official: true  },
  { id: 'walmart',   name: 'Walmart',    color: '#0071ce', bg: 'rgba(0,113,206,.1)',  official: true  },
  { id: 'homedepot', name: 'Home Depot', color: '#f96302', bg: 'rgba(249,99,2,.1)',   official: true  },
  { id: 'target',    name: 'Target',     color: '#cc0000', bg: 'rgba(204,0,0,.1)',    official: true  },
  { id: 'bestbuy',   name: 'Best Buy',   color: '#0046be', bg: 'rgba(0,70,190,.1)',   official: false },
  { id: 'costco',    name: 'Costco',     color: '#005daa', bg: 'rgba(0,93,170,.1)',   official: false },
  { id: 'etsy',      name: 'Etsy',       color: '#f56400', bg: 'rgba(245,100,0,.1)',  official: false },
  { id: 'newegg',    name: 'Newegg',     color: '#ff6600', bg: 'rgba(255,102,0,.1)',  official: false },
  { id: 'chewy',     name: 'Chewy',      color: '#00aae4', bg: 'rgba(0,170,228,.1)',  official: false },
  { id: 'ulta',      name: 'Ulta Beauty',color: '#e2007c', bg: 'rgba(226,0,124,.1)',  official: true  },
  { id: 'sephora',   name: 'Sephora',    color: '#000000', bg: 'rgba(0,0,0,.07)',      official: true  },
  { id: 'macys',     name: "Macy's",     color: '#e21a2c', bg: 'rgba(226,26,44,.1)',   official: true  },
];

export const DEFAULT_STORES = KNOWN_STORES.filter(s =>
  ['amazon', 'walmart', 'homedepot', 'target'].includes(s.id)
);

export const EXTRA_COLORS = [
  '#7c3aed','#0d9488','#be185d','#b45309','#1d4ed8','#059669','#dc2626','#0891b2',
];

export function hex2rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Agent router ─────────────────────────────────────────────────
const AGENT_MAP = {
  amazon:    runAmazonAgent,
  walmart:   runWalmartAgent,
  homedepot: runHomedepotAgent,
  target:    runTargetAgent,
  ulta:      runUltaAgent,
  sephora:   runSephoraAgent,
  macys:     runMacysAgent,
};

export function simulateAgent(store, query) {
  const agent = AGENT_MAP[store.id];
  if (agent) return agent(store, query);
  console.warn(`No agent found for store: ${store.id}`);
  return Promise.resolve([]);
}

// Smart router — returns only relevant store IDs for a query
// Saves 40–75% of API calls by skipping irrelevant marketplaces
export function getRelevantStores(query, activeStores) {
  const activeIds    = activeStores.map(s => s.id);
  const routedIds    = routeQuery(query, activeIds);
  return activeStores.filter(s => routedIds.includes(s.id));
}

// ─── Compute ranked results + arbitrage ──────────────────────────
export function computeResults(agentData, activeStores) {
  const flat = [];
  activeStores.forEach((s) => {
    (agentData[s.id] || []).forEach((row) =>
      flat.push({ ...row, store: s.id })
    );
  });
  flat.sort((a, b) => a.price - b.price);
  flat.forEach((r, i) => { r.rank = i + 1; });

  const groups = {};
  flat.forEach((r) => {
    const key = r.item.toLowerCase().split(' ').slice(0, 3).join(' ');
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });

  const arbList = [];
  for (const group of Object.values(groups)) {
    if (group.length < 2) continue;
    const prices = group.map((x) => x.price);
    const minP   = Math.min(...prices);
    const maxP   = Math.max(...prices);
    const spread = ((maxP - minP) / minP) * 100;
    if (spread >= 10) {
      arbList.push({
        name:     group[0].item,
        spread,
        saving:   +(maxP - minP).toFixed(2),
        cheapest: group.find((x) => x.price === minP),
        priciest: group.find((x) => x.price === maxP),
      });
    }
  }

  if (!arbList.length && flat.length > 1) {
    const a = flat[0], b = flat[flat.length - 1];
    if (a.store !== b.store) {
      arbList.push({
        name:     'Best vs highest price',
        spread:   ((b.price - a.price) / a.price) * 100,
        saving:   +(b.price - a.price).toFixed(2),
        cheapest: a,
        priciest: b,
      });
    }
  }

  arbList.sort((a, b) => b.saving - a.saving);
  return { ranked: flat, arbitrage: arbList.slice(0, 4) };
}
